import { fmtMoney, fmtDate, STATUS_LABELS, STATUS_STYLES } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

const LineItemsTable = ({ order }) => (
  <div className="px-5 pb-5 border-t border-border pt-4">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-muted-foreground">
          <th className="pb-2 font-medium">Item</th>
          <th className="pb-2 font-medium">Category</th>
          <th className="pb-2 font-medium text-right">Qty</th>
          <th className="pb-2 font-medium text-right">Price</th>
          <th className="pb-2 font-medium text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        {order.line_items.map((li, i) => (
          <tr key={`${li.product_name}-${li.sku}-${i}`} className="border-t border-border/60">
            <td className="py-2">{li.product_name}</td>
            <td className="py-2 text-muted-foreground">{li.category}</td>
            <td className="py-2 text-right">{li.quantity}</td>
            <td className="py-2 text-right">{fmtMoney(li.unit_price)}</td>
            <td className="py-2 text-right">{fmtMoney(li.line_total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="flex justify-end mt-3 text-xs text-muted-foreground gap-4">
      <span>Subtotal {fmtMoney(order.subtotal)}</span>
      <span>Shipping {fmtMoney(order.shipping)}</span>
      <span>Tax {fmtMoney(order.tax)}</span>
      {order.discount > 0 && <span>Discount −{fmtMoney(order.discount)}</span>}
    </div>
  </div>
);

export const OrderHistory = ({ orders }) => (
  <>
    <h2 className="text-2xl sm:text-3xl font-serif font-semibold mb-5">Order history</h2>
    {orders.length === 0 ? (
      <Card className="shadow-sm"><CardContent className="p-8 text-muted-foreground text-sm" data-testid="no-orders-message">No orders yet.</CardContent></Card>
    ) : (
      <div className="space-y-3" data-testid="order-history-list">
        {orders.map((o) => (
          <Collapsible key={o.id}>
            <Card className="shadow-sm">
              <CollapsibleTrigger asChild>
                <button className="w-full text-left" data-testid={`order-row-${o.id}`}>
                  <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-sm">{fmtDate(o.order_date)} · {o.source_order_id}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{o.source_platform} · {o.line_items.length} item{o.line_items.length > 1 ? "s" : ""}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[o.status] || ""}`}>{STATUS_LABELS[o.status] || o.status}</span>
                      <div className="text-right">
                        <p className="font-semibold">{fmtMoney(o.total)}</p>
                        {o.refund_amount > 0 && <p className="text-xs text-destructive">−{fmtMoney(o.refund_amount)} refunded</p>}
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <LineItemsTable order={o} />
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}
      </div>
    )}
  </>
);
